/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useEffect, useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import { useLeftSidebarState } from '../left-sidebar-controller/index.tsx';
import { useRightSidebarState } from '../right-sidebar-controller/index.tsx';
import type { PreviewPanelState, RightSidebarState } from '../../../../common/types.tsx';
import { usePreviewPanelState } from '../preview-panel/index.tsx';
import { setRemoteLayoutState } from './set-remote-layout-state/index.tsx';

export const LayoutRemoteStateUpdater = (): null => {
	const accountId = useAccountId();
	const leftSidebar = useLeftSidebarState();
	const rightSidebar = useRightSidebarState();
	const previewPanel = usePreviewPanelState();

	const isLeftSidebarCollapsed = leftSidebar.isCollapsed;
	const leftSidebarWidth = leftSidebar.width;

	// This prevents eccessive state updates on page loads
	const [initialLoad, setInitialLoad] = useState(true);
	const [localLeftSidebarWidthState, setLocalLeftSidebarWidthState] = useState<number>();
	const [localLeftSidebarCollapsedState, setLocalLeftSidebarCollapsedState] = useState<boolean>();
	const [localRightSidebarState, setLocalRightSidebarState] = useState<RightSidebarState>();
	const [localPreviewPanelState, setLocalPreviewPanelState] = useState<PreviewPanelState>();

	// Original version (with feature gate disabled) - maintains existing behavior
	const sideBarChangedOld = useCallback(
		(
			externalLeftSidebarWidth: number,
			externalIsLeftSidebarCollapsed: boolean,
			externalRightSidebarState: RightSidebarState,
			externalPreviewPanelState: PreviewPanelState,
		): boolean => {
			let changeDetected = false;
			if (!isEqual(externalLeftSidebarWidth, localLeftSidebarWidthState)) {
				setLocalLeftSidebarWidthState(externalLeftSidebarWidth);
				changeDetected = true;
			}
			if (!isEqual(externalIsLeftSidebarCollapsed, localLeftSidebarCollapsedState)) {
				setLocalLeftSidebarCollapsedState(externalIsLeftSidebarCollapsed);
				changeDetected = true;
			}
			if (!isEqual(externalRightSidebarState, localRightSidebarState)) {
				setLocalRightSidebarState(externalRightSidebarState);
				changeDetected = true;
			}
			if (!isEqual(externalPreviewPanelState, localPreviewPanelState)) {
				setLocalPreviewPanelState(externalPreviewPanelState);
				changeDetected = true;
			}
			return changeDetected;
		},
		[
			localLeftSidebarCollapsedState,
			localLeftSidebarWidthState,
			localRightSidebarState,
			localPreviewPanelState,
		],
	);

	useEffect(() => {
		if (!accountId) {
			return;
		}
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (initialLoad) {
			setInitialLoad(false);
			setLocalLeftSidebarWidthState(leftSidebarWidth);
			setLocalLeftSidebarCollapsedState(isLeftSidebarCollapsed);
			setLocalRightSidebarState(rightSidebar);
			setLocalPreviewPanelState(previewPanel);
			return;
		}

		// When using the fixed version, ensure all values are defined before proceeding
		if (
			leftSidebarWidth === undefined ||
			isLeftSidebarCollapsed === undefined ||
			rightSidebar === undefined ||
			previewPanel === undefined ||
			localLeftSidebarWidthState === undefined ||
			localLeftSidebarCollapsedState === undefined ||
			localRightSidebarState === undefined ||
			localPreviewPanelState === undefined
		) {
			return;
		}

		// TypeScript now knows these values are defined
		const hasChanges = isSidebarChanged({
			externalLeftSidebarWidth: leftSidebarWidth,
			externalIsLeftSidebarCollapsed: isLeftSidebarCollapsed,
			externalRightSidebarState: rightSidebar,
			externalPreviewPanelState: previewPanel,
			currentLocalLeftSidebarWidth: localLeftSidebarWidthState,
			currentLocalLeftSidebarCollapsed: localLeftSidebarCollapsedState,
			currentLocalRightSidebarState: localRightSidebarState,
			currentLocalPreviewPanelState: localPreviewPanelState,
		});

		if (!hasChanges) {
			return;
		}

		// Update local state separately when using fixed version to prevent callback recreation
		// Only update each piece of state if it actually changed (preserves original granular behavior)
		if (!isEqual(leftSidebarWidth, localLeftSidebarWidthState)) {
			setLocalLeftSidebarWidthState(leftSidebarWidth);
		}
		if (!isEqual(isLeftSidebarCollapsed, localLeftSidebarCollapsedState)) {
			setLocalLeftSidebarCollapsedState(isLeftSidebarCollapsed);
		}
		if (!isEqual(rightSidebar, localRightSidebarState)) {
			setLocalRightSidebarState(rightSidebar);
		}
		if (!isEqual(previewPanel, localPreviewPanelState)) {
			setLocalPreviewPanelState(previewPanel);
		}

		setRemoteLayoutState({
			accountId,
			state: {
				rightSidebar,
				leftSidebar: {
					isCollapsed: isLeftSidebarCollapsed,
					width: leftSidebarWidth,
				},
				...(fg('jira-glance-panel') && { previewPanel }),
			},
		});
	}, [
		accountId,
		isLeftSidebarCollapsed,
		leftSidebarWidth,
		rightSidebar,
		initialLoad,
		previewPanel,
		sideBarChangedOld,
		localLeftSidebarWidthState,
		localLeftSidebarCollapsedState,
		localRightSidebarState,
		localPreviewPanelState,
	]);

	return null;
};

// Pure comparison function - moved outside component for better performance
const isSidebarChanged = (args: {
	externalLeftSidebarWidth: number;
	externalIsLeftSidebarCollapsed: boolean;
	externalRightSidebarState: RightSidebarState;
	externalPreviewPanelState: PreviewPanelState;
	currentLocalLeftSidebarWidth: number;
	currentLocalLeftSidebarCollapsed: boolean;
	currentLocalRightSidebarState: RightSidebarState;
	currentLocalPreviewPanelState: PreviewPanelState;
}): boolean => {
	const {
		externalLeftSidebarWidth,
		externalIsLeftSidebarCollapsed,
		externalRightSidebarState,
		externalPreviewPanelState,
		currentLocalLeftSidebarWidth,
		currentLocalLeftSidebarCollapsed,
		currentLocalRightSidebarState,
		currentLocalPreviewPanelState,
	} = args;
	// Only compare, don't modify state to prevent infinite loops
	return (
		!isEqual(externalLeftSidebarWidth, currentLocalLeftSidebarWidth) ||
		!isEqual(externalIsLeftSidebarCollapsed, currentLocalLeftSidebarCollapsed) ||
		!isEqual(externalRightSidebarState, currentLocalRightSidebarState) ||
		!isEqual(externalPreviewPanelState, currentLocalPreviewPanelState)
	);
};
